import LangToggle from '@components/atoms/langToogle';
import MobileStepHeader from '@components/navigation/mobile/MobileStepHeader';
import MobileStepperDrawer from '@components/navigation/mobile/MobileStepperDrawer';
import { SideStepper } from '@components/navigation/stepper';
import { PATH_TO_STEP_ID } from '@components/utils/stepperRouteMap';
import { getStepsList, type StepDefinition } from '@constants/stepsList';
import type { RootState } from '@store/index';
import type { StepInterface } from '@store/stepper/interfaces/stepperInterfaces';
import { selectIsStepperCompleted } from '@store/stepper/selectors/stepperSelectors';
import { stepsChange } from '@store/stepper/slices/stepperSlice';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

export function MainLayout() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isSteperCompleted = useSelector(selectIsStepperCompleted);
  const { pathname } = useLocation();
  const isSubmitting = useSelector(
    (state: RootState) => state.user.isSubmitting,
  );
  const [isStepperOpen, setIsStepperOpen] = useState(false);

  const routeActiveId = useMemo(() => {
    const path =
      pathname !== '/' && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;
    return PATH_TO_STEP_ID[path] ?? 'step1';
  }, [pathname]);

  const stepsList: StepDefinition[] = getStepsList(t);
  const stepsForStore: StepInterface[] = stepsList.map(({ id, text }) => ({
    id,
    text,
    icon: 'custom',
  }));

  useEffect(() => {
    dispatch(stepsChange(stepsForStore));
  }, [dispatch, stepsForStore]);

  const activeStep = stepsList.find(s => s.id === routeActiveId);
  const mobileSubtitle = activeStep?.text ?? '';

  const normalizedPath =
    pathname.endsWith('/') && pathname !== '/'
      ? pathname.slice(0, -1)
      : pathname;
  const isValidationRoute =
    normalizedPath === '/validation' ||
    normalizedPath.startsWith('/validation/');

  return (
    <main className="bg-gray-200 min-h-screen flex flex-col md:flex-row gap-0 md:gap-x-4 lg:gap-x-6 py-0 px-0 sm:py-4 sm:px-6 md:px-8 lg:py-[56px] lg:px-[52px]">
      <MobileStepHeader
        subtitle={mobileSubtitle}
        className="block md:hidden"
        onMenuClick={() => setIsStepperOpen(true)}
      />
      {isStepperOpen && (
        <MobileStepperDrawer
          isOpen={isStepperOpen}
          onClose={() => setIsStepperOpen(false)}
          items={stepsList}
          activeId={routeActiveId}
          completed={isSteperCompleted}
          title={t('stepper.title')}
          className="md:hidden"
        />
      )}

      {/* Reduced stepper */}
      <div className="hidden md:flex lg:hidden flex-shrink-0">
        <SideStepper
          collapsed
          activeId={routeActiveId}
          completed={isSteperCompleted}
          items={stepsList}
          title={t('stepper.title')}
        />
      </div>

      {/* Complete stepper on desktop */}
      <div className="hidden lg:flex flex-shrink-0">
        <SideStepper
          activeId={routeActiveId}
          completed={isSteperCompleted}
          items={stepsList}
          title={t('stepper.title')}
        />
      </div>

      <section className="bg-white w-full rounded-none sm:rounded-[24px] p-4 sm:p-8 md:p-10 flex flex-col min-h-[80vh] lg:min-h-[88vh] relative">
        {/* Hide host toggle when in validation route */}
        {!isSubmitting && !isValidationRoute && (
          <div className="block absolute top-6 sm:top-8 right-6 sm:right-8 lg:top-10 lg:right-10 z-20 cursor-pointer">
            <LangToggle />
          </div>
        )}
        <div className="flex flex-col items-center w-full flex-1 min-h-0">
          <Outlet />
        </div>
      </section>
    </main>
  );
}

export default MainLayout;
